// Formulaire de création/édition d'un message système (admin)
import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import TipTapEditor from '../TipTapEditor';
import {
  createMessage,
  updateMessage,
  uploadMessageImage,
} from '../../Services/systemMessageService';
import { toast } from 'sonner';
import { Loader2, Upload, X, Eye } from 'lucide-react';
import MessageCard from '../MessageCard';
import { Timestamp } from 'firebase/firestore';

// ─── Schéma de validation ────────────────────────────────
const schema = z.object({
  title: z.string().min(3, 'Titre requis (min. 3 caractères)').max(120),
  type: z.enum(['feature', 'news', 'maintenance', 'promo', 'update']),
  priority: z.enum(['low', 'normal', 'high']),
  targetAudience: z.enum(['all', 'free', 'premium']),
  publishedAt: z.string().optional(),
  expiresAt: z.string().optional(),
  actionLabel: z.string().optional(),
  actionLink: z.string().optional(),
});

// ─── Valeurs par défaut du formulaire ───────────────────
const getDefaultValues = (message) => {
  if (!message) {
    return {
      title: '',
      type: 'news',
      priority: 'normal',
      targetAudience: 'all',
      publishedAt: '',
      expiresAt: '',
      actionLabel: '',
      actionLink: '',
    };
  }
  return {
    title: message.title || '',
    type: message.type || 'news',
    priority: message.priority || 'normal',
    targetAudience: message.targetAudience || 'all',
    publishedAt: message.publishedAt?.toDate
      ? toDateInputValue(message.publishedAt.toDate())
      : '',
    expiresAt: message.expiresAt?.toDate
      ? toDateInputValue(message.expiresAt.toDate())
      : '',
    actionLabel: message.actionButton?.label || '',
    actionLink: message.actionButton?.link || '',
  };
};

// Convertit une Date en valeur pour <input type="datetime-local">
const toDateInputValue = (date) => {
  if (!date) return '';
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

/**
 * @param {{ open: boolean, onClose: Function, onSuccess: Function, editMessage: Object|null }} props
 */
const MessageComposer = ({ open, onClose, onSuccess, editMessage = null }) => {
  const isEdit = !!editMessage;

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: getDefaultValues(editMessage),
  });

  // Contenu TipTap (géré en dehors de react-hook-form)
  const [content, setContent] = useState(editMessage?.content || '');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(editMessage?.imageUrl || null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Observer les valeurs du formulaire pour la prévisualisation
  const watchedTitle = watch('title');
  const watchedType = watch('type');
  const watchedPriority = watch('priority');
  const watchedActionLabel = watch('actionLabel');
  const watchedActionLink = watch('actionLink');

  // Réinitialiser le formulaire à l'ouverture
  useEffect(() => {
    if (open) {
      reset(getDefaultValues(editMessage));
      setContent(editMessage?.content || '');
      setImagePreview(editMessage?.imageUrl || null);
      setImageFile(null);
      setSaving(false);
      setShowPreview(false);
    }
  }, [open, editMessage, reset]);

  // Gérer le changement d'image
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5 Mo");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // Supprimer l'image
  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  // Soumettre le formulaire
  const onSubmit = async (formData, publish) => {
    if (!content || content === '<p></p>' || content === '') {
      toast.error('Le contenu du message est requis');
      return;
    }
    setSaving(true);
    try {
      // Upload image si nécessaire
      let imageUrl = editMessage?.imageUrl || null;
      if (imageFile) {
        imageUrl = await uploadMessageImage(imageFile, editMessage?.id || 'new');
      } else if (!imagePreview) {
        imageUrl = null;
      }

      const data = {
        title: formData.title,
        content,
        type: formData.type,
        priority: formData.priority,
        targetAudience: formData.targetAudience,
        publishedAt: formData.publishedAt || null,
        expiresAt: formData.expiresAt || null,
        imageUrl,
        actionButton: formData.actionLabel
          ? { label: formData.actionLabel, link: formData.actionLink || '' }
          : null,
      };

      if (isEdit) {
        await updateMessage(editMessage.id, data, publish ? true : null);
        toast.success(publish ? 'Message publié !' : 'Brouillon enregistré');
      } else {
        await createMessage(data, publish);
        toast.success(publish ? 'Message publié !' : 'Brouillon enregistré');
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Erreur sauvegarde message:', err);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Message de prévisualisation
  const previewMessage = {
    id: 'preview',
    title: watchedTitle || 'Titre du message',
    content,
    type: watchedType,
    priority: watchedPriority,
    isRead: false,
    publishedAt: Timestamp.now(),
    actionButton:
      watchedActionLabel ? { label: watchedActionLabel, link: watchedActionLink } : null,
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !val && !saving && onClose()}>
      <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 p-0">
        {/* En-tête */}
        <DialogHeader className="p-6 pb-4 border-b border-gray-200 dark:border-gray-700">
          <DialogTitle className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {isEdit ? 'Modifier le message' : 'Créer un message'}
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 space-y-5">
          {/* Titre */}
          <div>
            <Label htmlFor="title" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
              Titre <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              {...register('title')}
              placeholder="Ex: 🎉 Nouvelle fonctionnalité disponible !"
              className={`bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 placeholder-gray-400 dark:placeholder-gray-500 ${errors.title ? 'border-red-500 dark:border-red-500' : ''}`}
            />
            {errors.title && (
              <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>
            )}
          </div>

          {/* Ligne : Type + Priorité + Audience */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
                Type <span className="text-red-500">*</span>
              </Label>
              <select
                {...register('type')}
                className="w-full h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-1 text-sm shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
              >
                <option value="news">📢 Actualité</option>
                <option value="feature">🆕 Nouveauté</option>
                <option value="maintenance">🔧 Maintenance</option>
                <option value="promo">💎 Promotion</option>
                <option value="update">⚠️ Important</option>
              </select>
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
                Priorité
              </Label>
              <select
                {...register('priority')}
                className="w-full h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-1 text-sm shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
              >
                <option value="low">Basse</option>
                <option value="normal">Normale</option>
                <option value="high">Haute</option>
              </select>
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
                Audience
              </Label>
              <select
                {...register('targetAudience')}
                className="w-full h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-1 text-sm shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
              >
                <option value="all">Tous</option>
                <option value="free">Gratuits uniquement</option>
                <option value="premium">Premium uniquement</option>
              </select>
            </div>
          </div>

          {/* Contenu TipTap */}
          <div>
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
              Contenu <span className="text-red-500">*</span>
            </Label>
            <div className="rounded-xl overflow-hidden border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800">
              <TipTapEditor value={content} onChange={setContent} />
            </div>
          </div>

          {/* Image optionnelle */}
          <div>
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
              Image (optionnel)
            </Label>
            {imagePreview ? (
              <div className="relative inline-block">
                <img
                  src={imagePreview}
                  alt="Aperçu"
                  className="h-32 w-auto rounded-lg object-cover border border-gray-200 dark:border-gray-700"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  aria-label="Supprimer l'image"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <label className="cursor-pointer flex items-center gap-2 w-fit px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 transition-colors text-sm text-gray-600 dark:text-gray-400">
                <Upload className="w-4 h-4" />
                Choisir une image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>
            )}
          </div>

          {/* Dates de publication et d'expiration */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
                Date de publication
              </Label>
              <Input
                type="datetime-local"
                {...register('publishedAt')}
                className="text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 [&::-webkit-calendar-picker-indicator]:dark:filter [&::-webkit-calendar-picker-indicator]:dark:invert"
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Laisser vide = publication immédiate
              </p>
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
                Date d'expiration
              </Label>
              <Input
                type="datetime-local"
                {...register('expiresAt')}
                className="text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 [&::-webkit-calendar-picker-indicator]:dark:filter [&::-webkit-calendar-picker-indicator]:dark:invert"
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Par défaut : +6 mois
              </p>
            </div>
          </div>

          {/* Bouton d'action optionnel */}
          <div>
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
              Bouton d'action (optionnel)
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Input
                  {...register('actionLabel')}
                  placeholder="Label du bouton (ex: En savoir plus)"
                  className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>
              <div>
                <Input
                  {...register('actionLink')}
                  placeholder="Lien (ex: /pricing ou https://...)"
                  className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>
            </div>
          </div>

          {/* Prévisualisation */}
          {showPreview && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-gray-50 dark:bg-gray-800/50">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                Aperçu de la carte
              </p>
              <MessageCard message={previewMessage} onClick={() => {}} />
            </div>
          )}
        </div>

        {/* Pied de page avec actions */}
        <div className="p-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Eye className="w-4 h-4" />
            {showPreview ? 'Masquer' : 'Aperçu'}
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={saving}
              onClick={() => handleSubmit((d) => onSubmit(d, false))()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Enregistrer brouillon
            </button>

            <button
              type="button"
              disabled={saving}
              onClick={() => handleSubmit((d) => onSubmit(d, true))()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Publier
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MessageComposer;
